/* eslint-disable no-unused-vars */
import { useEffect, useState, useCallback } from 'react';
import { paymentsAPI } from 'services/payments.service.js';
import { WireTransfer, PayPal } from 'assets/icons';
import { Loader, Radio } from 'components';
import { PayPalForm } from './PayPalForm';
import { WireForm } from './WireForm';
import { WireDetails } from './WireDetails';
import { PayPalDetails } from './PayPalDetails';
import { showPolipop } from 'utils';
import * as S from './PublisherPayments.styled';

const wireFormFields = [
  {
    title: 'Beneficiary Name',
    id: 'beneficiaryName',
    required: true,
    pattern: /^[A-Za-z ]+$/,
  },
  {
    title: 'Bank name',
    id: 'bankName',
    required: true,
    pattern: /^[A-Za-z ]+$/,
  },
  {
    title: 'Bank address',
    id: 'bankAddress',
    required: true,
  },
  {
    title: 'Bank Iban Number',
    id: 'bankIbanNumber',
    required: true,
    pattern: /^[A-Za-z0-9]{15,34}$/,
  },
  {
    title: 'Bank Swift Code',
    id: 'bankSwiftCode',
    required: true,
    pattern: /^[A-Za-z0-9]{8,11}$/,
  },
  {
    title: 'Bank Account Number',
    id: 'bankAccountNumber',
    required: true,
    type: 'number',
  },
  {
    title: 'Bank Routing Number',
    hint: 'US and Canadian Bank account',
    id: 'bankRoutingNumber',
    required: false,
    pattern: /^\d{9}$/,
  },
  {
    title: 'Payment details',
    id: 'paymentDetails',
    tag: 'textarea',
    required: false,
  },
];

export const PublisherPayments = () => {
  const [isWireOpen, setIsWireOpen] = useState(false);
  const [isPayPalOpen, setIsPayPalOpen] = useState(false);

  const {
    data: fetchedData,
    isFetching,
    isError,
  } = paymentsAPI.useGetPublisherPaymentsDetailsQuery();
  const {
    data: fetchedPaymentType,
    isFetching: isFetchingPaymentType,
    isError: isErrorPaymentType,
  } = paymentsAPI.useGetPaymentTypeQuery();

  const [SavePaymentAction, result] = paymentsAPI.useSavePaymentMutation();
  const [SaveDefaultPaymentTypeAction, defaultTypeResult] =
    paymentsAPI.useSaveDefaultPaymentTypeMutation();

  const isWireConfigured =
    !!fetchedData?.bankAccountNumber ||
    !!fetchedData?.bankAddress ||
    !!fetchedData?.bankIbanNumber ||
    !!fetchedData?.bankName ||
    !!fetchedData?.bankSwiftCode ||
    !!fetchedData?.beneficiaryName;

  const isPayPalConfigured = !!fetchedData?.email;
  const isRadioButtonsActive =
    !defaultTypeResult?.isLoading &&
    !result?.isLoading &&
    !isFetching &&
    !isFetchingPaymentType &&
    !isError &&
    !isErrorPaymentType &&
    isWireConfigured &&
    isPayPalConfigured;

  const handleSavePayments = useCallback(
    (data, paymentType) => {
      // if paypal and wire not configured yet - set current type as default, otherwise - don't change
      const defaultPaymentType =
        !isPayPalConfigured && !isWireConfigured
          ? paymentType.toString()
          : fetchedPaymentType?.paymentTypeId;

      const dataToSend = {
        ...fetchedData,
        ...data,
        paymentType: defaultPaymentType,
      };

      SavePaymentAction(dataToSend);
    },
    [isPayPalConfigured, isWireConfigured, fetchedData, fetchedPaymentType?.paymentTypeId],
  );

  const handleDeletePayment = useCallback(
    (paymentType) => {
      let defaultPaymentType = '1';

      if (!isPayPalConfigured && isWireConfigured && paymentType == 1) defaultPaymentType = null; // if deleting wire and paypal not configured
      if (isPayPalConfigured && !isWireConfigured && paymentType == 2) defaultPaymentType = null; // if deleting paypal and wire not configured

      if (isPayPalConfigured && isWireConfigured && paymentType == 1) defaultPaymentType = '2';
      if (isPayPalConfigured && !isWireConfigured && paymentType == 1) defaultPaymentType = '2'; // not real case

      let dataToSend = { ...fetchedData, paymentType: defaultPaymentType };

      if (paymentType == 1) {
        wireFormFields.forEach((field) => {
          dataToSend[field.id] = '';
        });
      }
      if (paymentType == 2) {
        dataToSend.email = '';
      }

      SavePaymentAction(dataToSend);
    },
    [isPayPalConfigured, isWireConfigured, fetchedData],
  );

  function handleSaveDefaultPayment(value) {
    if (!fetchedData?.appn_user_email) return;

    SaveDefaultPaymentTypeAction({
      paymentTypeId: value.toString(),
      userEmail: fetchedData?.appn_user_email,
    });
  }

  useEffect(() => {
    // update states here after showing loader, not in handlers to avoid layout jump
    if (defaultTypeResult.isSuccess || result.isSuccess) {
      setIsPayPalOpen(false);
      setIsWireOpen(false);
    }
  }, [defaultTypeResult.isSuccess, result.isSuccess]);

  useEffect(() => {
    if (defaultTypeResult.isError || defaultTypeResult.isSuccess) showPolipop(defaultTypeResult);
  }, [defaultTypeResult.isError, defaultTypeResult.isSuccess]);

  useEffect(() => {
    if (result.isError || result.isSuccess) showPolipop(result);
  }, [result.isError, result.isSuccess]);

  if (isError) {
    return <h2 style={{ border: '1px solid red', color: 'red', textAlign: 'center' }}>Error</h2>;
  }

  return (
    <>
      <S.Text>
        Please choose and set your default payment method to receive your publisher revenues.
        <br />
        For more information about Appnext&apos;s payment terms,{' '}
        <S.ExternalLink
          target='_blank'
          rel='noopener noreferrer'
          href='https://wiki.appnext.com/hc/en-us/articles/207200209-Publishers-Payment-Terms'
        >
          read this article.
        </S.ExternalLink>
      </S.Text>
      {isFetching ? (
        <S.LoaderBox>
          <Loader.Local delay={100} />
        </S.LoaderBox>
      ) : (
        <S.PaymentMethodsWrap>
          <S.PaymentMethod>
            <S.PaymentIconWrap>
              <WireTransfer />
              <S.PaymentsType>
                <Radio
                  id='wire-default'
                  checked={fetchedPaymentType?.paymentTypeId == 1}
                  disabled={!isRadioButtonsActive}
                  onChange={() => handleSaveDefaultPayment(1)}
                />
                <S.Label htmlFor='wire-default' isActive={isRadioButtonsActive}>
                  {fetchedPaymentType?.paymentTypeId == 1 ? 'Default' : 'Set as Default'}
                </S.Label>
              </S.PaymentsType>
            </S.PaymentIconWrap>
            <S.SubHeading>Wire Transfer</S.SubHeading>
            {isWireOpen ? (
              <>
                <S.SmallText>Enter your Bank Details</S.SmallText>
                <WireForm
                  wireFormFields={wireFormFields}
                  onHandleSavePayments={handleSavePayments}
                  onHandleDeletePayment={handleDeletePayment}
                  onSetIsWireOpen={setIsWireOpen}
                  defaultData={fetchedData}
                  isButtonDisabled={
                    defaultTypeResult?.isLoading || result?.isLoading || isFetchingPaymentType
                  }
                />
              </>
            ) : (
              <WireDetails
                data={fetchedData}
                isWireConfigured={isWireConfigured}
                wireFormFields={wireFormFields}
                onHandleDeletePayment={handleDeletePayment}
                onSetIsWireOpen={setIsWireOpen}
              />
            )}
          </S.PaymentMethod>
          <S.PaymentMethod>
            <S.PaymentIconWrap>
              <PayPal />
              <S.PaymentsType>
                <Radio
                  id='paypal-default'
                  checked={fetchedPaymentType?.paymentTypeId == 2}
                  disabled={!isRadioButtonsActive}
                  onChange={() => handleSaveDefaultPayment(2)}
                />
                <S.Label htmlFor='paypal-default' isActive={isRadioButtonsActive}>
                  {fetchedPaymentType?.paymentTypeId == 2 ? 'Default' : 'Set as Default'}
                </S.Label>
              </S.PaymentsType>
            </S.PaymentIconWrap>
            <S.SubHeading>Pay Pal</S.SubHeading>
            {isPayPalOpen ? (
              <>
                <S.SmallText> Enter your PayPal account email</S.SmallText>
                <PayPalForm
                  userEmail={fetchedData.appn_user_email}
                  currentPayPalEmail={fetchedData.email}
                  onHandleSavePayments={handleSavePayments}
                  onSetIsPayPalOpen={setIsPayPalOpen}
                  isButtonDisabled={
                    defaultTypeResult?.isLoading || result?.isLoading || isFetchingPaymentType
                  }
                />
              </>
            ) : (
              <PayPalDetails
                paypalEmail={fetchedData?.email}
                onHandleDeletePayment={handleDeletePayment}
                onSetIsPayPalOpen={setIsPayPalOpen}
              />
            )}
          </S.PaymentMethod>
        </S.PaymentMethodsWrap>
      )}
    </>
  );
};
